/** THE NATIVE MEDIA ENGINE — stage 3b, the JS half.
 *
 * Satisfies `MediaEngine` by proxying to `RexyNativeCall`, so `voiceDaemon.ts`
 * does not learn a second way to negotiate: it calls `open()`, carries the SDP
 * to the box on the socket it already owns, and calls `accept()`. Signalling
 * never leaves the WebView (Q2) and this file never sees the box.
 *
 * ── WHAT IS DELIBERATELY ABSENT ─────────────────────────────────────────────
 * `micStream()` returns **null**, always. There is no `MediaStream` in
 * JavaScript when libwebrtc owns the microphone, and the tempting repair — a
 * `getUserMedia` "just for the level" — is a SECOND CAPTURE and a second AEC
 * instance on one device (owner ruling 2026-07-29, blocker 3). The level comes
 * from the engine's own stats instead, over `onLevel`, and the test suite locks
 * `getUserMedia` to two files so a third site fails a test rather than a call.
 *
 * There is also no method here that a consumer could call to START capturing.
 * Level arrives as a callback; it is never fetched.
 */
import { registerPlugin, Capacitor } from '@capacitor/core';
import { startNativeCall, setNativeAudioRoute } from './nativeCall';
import { MediaEngine, MediaEngineOpts } from './mediaEngine';
import {
  probeBegin, probeMark, probeEnd, countCandidates, probeNoteNative,
  probeNoteNativeStats, probeNoteRouteFailed, probeNoteGainFailed,
  probeNoteDuckRefused, probeNoteMuteUndelivered,
  probeNotePlaybackAssert, probeNotePlaybackUndelivered,
  probeNoteFirstRtp, probeNoteLevel,
} from './mediaProbe';
import { gainAuditReset, gainAuditSample, gainAuditCommand } from './gainAudit';

interface MediaPlugin {
  getDiagnostics(): Promise<{ mediaEngine?: boolean; playbackVerb?: boolean }>;
  startMedia(o: { documentId: string }): Promise<{ sdp: string }>;
  acceptAnswer(o: { sdp: string }): Promise<void>;
  setMediaMuted(o: { muted: boolean }): Promise<void>;
  duckPlayback(o: { ducked: boolean; turnKey?: string }): Promise<void>;
  setLevelReporting(o: { enabled: boolean }): Promise<void>;
  stopMedia(o: { reason: string }): Promise<void>;
  releaseOrphanedMedia(o: { documentId: string }): Promise<void>;
  addListener(event: string, fn: (data: any) => void): Promise<{ remove: () => void }>;
}

const plugin = registerPlugin<MediaPlugin>('NativeCall');

/** THIS DOCUMENT'S IDENTITY (F2).
 *
 *  Minted once per document load and never persisted. It is the supersede key:
 *  a claim bearing a different id tears the incumbent engine down before the
 *  replacement is built, so a reload, a navigation or a `?surface` swap cannot
 *  strand a native peer holding the microphone with nothing in JS able to close
 *  it. Persisting it would defeat the whole mechanism — a reloaded document
 *  would look like the same document. */
const DOCUMENT_ID = `doc_${Math.random().toString(36).slice(2)}_${Date.now()}`;

/** ── THE ROUTE A CALL STARTS ON (owner ruling 2026-08-07, option (b)) ──────
 *
 *  TRUE = speaker. The owner ruled against the recommendation and gave the
 *  reason: *"I want her loud when I tap, not loud after I find a button. A
 *  default I have to correct on every call is the same annoyance as no control at
 *  all, just with more taps."* The speaker button's job is the OTHER direction —
 *  sending the call back to the receiver for privacy, the rarer move.
 *
 *  IT LIVES IN JS ON PURPOSE. *"If it turns out wrong on the device, it is one
 *  constant and I will tell you."* In JS that is an edit and a reload; in Swift it
 *  is a Codemagic build. This arc has already paid a full build cycle for a fix
 *  that sat where nothing served it. */
const DEFAULT_SPEAKER_ROUTE = true;

/** ── THE START RESULT, PUBLISHED (build 19, Ghost round 4) ────────────────
 *
 *  PhoneHost used to learn what CallKit said by CALLING startNativeCall itself.
 *  Two reporters is what let two starts overlap before `callId` existed, so the
 *  second reporter is gone — but the readout still has to say what happened, and
 *  the teardown still has to know a CallKit call exists.
 *
 *  So the one caller PUBLISHES its result and the surface observes it. ONE FACT,
 *  ONE SOURCE: the screen renders the value the negotiation sequence actually
 *  used, never a second opinion obtained by asking again. Same rule as
 *  `mediaEngineCapability()` above, for the same reason.
 *
 *  The last value is REPLAYED to a late subscriber. PhoneHost's effect can mount
 *  after the sequence has begun — that timing is precisely why it is no longer
 *  the reporter — so a subscriber that arrived late must not be told nothing. */
export type CallKitStartResult =
  | { state: 'requested' }
  | { state: 'ok'; epoch: number }
  | { state: 'rejected'; error: string };

let lastStartResult: CallKitStartResult | null = null;
const startResultSubs = new Set<(r: CallKitStartResult) => void>();

export function onCallKitStartResult(fn: (r: CallKitStartResult) => void): () => void {
  startResultSubs.add(fn);
  if (lastStartResult) fn(lastStartResult);
  return () => { startResultSubs.delete(fn); };
}

function publishStartResult(r: CallKitStartResult): void {
  lastStartResult = r;
  startResultSubs.forEach((fn) => { try { fn(r); } catch { /* a listener must never break the call */ } });
}

/** ── THE VERSION SKEW GATE ────────────────────────────────────────────────
 *
 *  THE PHONE LOADS ITS FRONT END FROM THE BOX AT RUNTIME AND ITS SWIFT FROM THE
 *  BINARY (HIGHEST canon, 2026-08-03). So this file can be a whole release ahead
 *  of the plugin it talks to — and `Capacitor.isPluginAvailable('NativeCall')`
 *  CANNOT SEE THAT: it answers about the plugin, which has existed since stage 1,
 *  not about whether the plugin registers `startMedia`. On a pre-3b binary it
 *  returns true and the very first call rejects with "not implemented".
 *
 *  That would have taken voice off the phone on the next reload, in EITHER arm —
 *  the A/B gates CallKit, not the engine selection — and the owner's standing
 *  requirement is that arm B keeps voice working throughout.
 *
 *  So it STARTS FALSE and is only raised by a binary that says it carries the
 *  engine. Until proven, the browser engine is used, which is exactly today's
 *  working behaviour. Fail toward the path that works. */
let mediaCapable = false;

/** ── THE SECOND CAPABILITY, AND WHY IT IS NOT THE FIRST (Fix 2) ─────────────
 *
 *  True only on a binary that states its own playback over the peer's control
 *  channel. `mediaCapable` above CANNOT stand in for it: a 3b binary answers
 *  `mediaEngine: true` and carries no `playing` verb whatsoever.
 *
 *  This gates ONE thing — whether this window stops asserting playback over the
 *  socket. The web half of that suppression arrives BY RELOAD and the Swift half
 *  only by a build (capacitor.config.ts server.url; canon 2026-08-03). Gate it
 *  on the wrong flag and a reload onto the CURRENT binary leaves nobody
 *  asserting: she speaks, the box acks no mark, and the transcript records
 *  silence. Starts false and is raised only by a key that is present AND true,
 *  so every older binary keeps today's behaviour exactly. */
let playbackVerbCapable = false;

/** Does the binary under this document state its own playback? Read at the one
 *  place that decides whether this window may speak for the track. */
export function nativePlaybackVerb(): boolean {
  return playbackVerbCapable;
}

/** WHAT THE BINARY SAID, IN THREE DISTINCT ANSWERS THAT ARE NEVER COLLAPSED.
 *
 *  Ghost's constraint on this round, and the reason this is a union rather than a
 *  boolean: an ABSENT key must never be phrased as a negative ANSWER. A pre-3b
 *  binary does not say "no" — it says nothing, because the key did not exist when
 *  it was built. Rendering that as `diagnostics: false` would invent a reply the
 *  binary never made, which is the same fault as inventing a reassuring one.
 *
 *  `declined` cannot be produced by any binary built from this source (the Swift
 *  hard-codes `true` at RexyCallController.swift:199). It exists so that if one
 *  ever DOES answer false, the readout has somewhere honest to put it instead of
 *  quietly reusing the word for absence. */
export type MediaEngineCapability =
  /** The key is present and true — this binary carries the stage-3b engine. */
  | 'carried'
  /** The plugin answered and the key WAS NOT THERE. A pre-3b binary. */
  | 'absent'
  /** The plugin answered and said false. Never inferred from absence. */
  | 'declined';

/** THE ONE ANSWER, RESOLVED ONCE. The readout awaits THIS rather than firing its
 *  own second getDiagnostics(): two round trips are two chances to disagree, and
 *  the screen must show the value the engine selection actually used, not a
 *  second opinion about it. `null` means the plugin never answered at all —
 *  which the CallKit line already reports as PLUGIN NOT IN BINARY, so it is not
 *  restated as a capability verdict. */
let capabilityAnswer: Promise<MediaEngineCapability | null> | null = null;

export function mediaEngineCapability(): Promise<MediaEngineCapability | null> {
  return capabilityAnswer ?? Promise.resolve(null);
}

export function nativeMediaAvailable(): boolean {
  return Capacitor.isNativePlatform()
    && Capacitor.isPluginAvailable('NativeCall')
    && mediaCapable;
}

/**
 * EVERY FRESH DOCUMENT ANNOUNCES ITSELF, before it wants anything.
 *
 * Capacitor's own `webViewWebContentProcessDidTerminate` does `bridge.reset()`
 * then `webView.reload()`, so a web-layer crash produces a NEW document — but
 * supersede-on-claim only fires when a document starts a call, and a document
 * that never taps never claims. This is the hello that releases whatever a dead
 * document left holding the device, whether or not this one intends to call.
 *
 * Deliberately not a `WKNavigationDelegate` hook: Capacitor owns
 * `navigationDelegate`, overriding it would break its navigation handling, and a
 * forwarding proxy is invasive and unverifiable without a device.
 */
export function announceDocument(): void {
  if (!Capacitor.isNativePlatform() || !Capacitor.isPluginAvailable('NativeCall')) return;
  // Probe FIRST — the flag starts false, so this is what raises it, and a
  // pre-3b binary simply never raises it. Deliberately not gated on
  // nativeMediaAvailable(), which would be a probe that could never run.
  // ONE ROUND TRIP. The resolved answer is HELD so the readout can await the same
  // fact the selection below is made from — see mediaEngineCapability(). The
  // selection rule is byte-for-byte what it was: mediaCapable is raised only by a
  // key that is present AND true, and nothing else in this round moves it.
  // The optional call can return undefined on a binary with no such method, and
  // `.then` on undefined throws SYNCHRONOUSLY — inside the mount effect, where it
  // would take the probe down with it. A missing method is "never answered",
  // which is `null`, and is exactly what the old code's `mediaCapable = false`
  // already meant.
  const answer: Promise<any> | undefined = (plugin as any).getDiagnostics?.();
  capabilityAnswer = !answer ? Promise.resolve(null) : answer
    .then((info: any) => {
      mediaCapable = !!info?.mediaEngine;
      // The same round trip answers both. Raised only by present-AND-true, and
      // read at exactly one site (`playbackAssertionOwnedByPeer` below).
      playbackVerbCapable = !!info?.playbackVerb;
      // PRESENT-AND-FALSE AND NOT-PRESENT ARE DIFFERENT FINDINGS and are kept
      // apart at the source, where the difference is still visible. One step
      // later there is only a boolean and the distinction is unrecoverable.
      if (mediaCapable) return 'carried' as MediaEngineCapability;
      const answered = !!info && Object.prototype.hasOwnProperty.call(info, 'mediaEngine');
      return (answered ? 'declined' : 'absent') as MediaEngineCapability;
    })
    .catch(() => null);
  void plugin.releaseOrphanedMedia({ documentId: DOCUMENT_ID }).catch(() => {
    /* nothing held, or the plugin is absent — either way there is nothing to release */
  });
}

export class NativeMediaEngine implements MediaEngine {
  private opts: MediaEngineOpts;
  private listeners: Array<{ remove: () => void }> = [];
  private connected = false;
  private closing = false;
  /** The last playback fact the NATIVE side stated on this peer, or null if it
   *  has stated none. Only read on the pre-`playing`-verb path, where this
   *  window is still the one speaking for the track. */
  private lastRendering: boolean | null = null;
  /** ── DELIVERY, NOT CAPABILITY (owner ruling 2026-08-10) ──────────────────
   *
   *  True only once the BOX has said it received and applied a `playing` verb
   *  on this peer. Until then this window keeps asserting over the socket, even
   *  on a binary that carries the verb — because `playbackVerb` only ever meant
   *  "this binary CAN send it", and on 2026-08-10 it could, while the box could
   *  not receive it (media.py had no `import json` and dropped every message
   *  silently). Nothing asserted playback and the transcript went to zero.
   *
   *  Per-peer: a renegotiation builds a fresh track at the box, owned by the
   *  window and not-yet-asserted, so this must be earned again. */
  private peerOwnsPlayback = false;

  /** ── UNITY HAS BEEN ASSERTED ON THIS PEER'S REMOTE TRACK (2026-08-14) ─────
   *
   *  Ten calls out of ten in the spool opened at `gain 0` and NONE has ever
   *  opened at unity. Nothing writes the remote track's volume when it arrives:
   *  `peerConnection(_:didAdd:streams:)` holds the track so the ramp has a source
   *  to act on, and the only writer in that file is `rampGain`. The browser
   *  engine does the opposite and always has — `gain.gain.setValueAtTime(1, …)`
   *  in the same breath as `createGain()` — so this is one missing statement, on
   *  one platform.
   *
   *  AND THE RESUME MEANT TO COVER IT CANNOT LAND. `session_ready` issues a
   *  keyless resume for exactly this, commented "a reconnect cannot inherit a
   *  duck… the engine early-returns when the gain is already at unity". The early
   *  return that actually fires is `guard let source = remoteTrack?.source`, and
   *  at open() there is no track yet — proven, not inferred: on all ten calls that
   *  resume's own audit row reports `gain: -1`, which IS "no remote track". So a
   *  dropped command and a cheap no-op are indistinguishable, and the gain was
   *  left to be lifted by the first `agent_speaking_start` — which is why seven
   *  calls came up only when she spoke (unity at seq 14-24, six to thirty seconds
   *  in) and three never came up at all, because she never did.
   *
   *  ONCE PER PEER, AND THAT IS WHAT MAKES IT SAFE. It fires on the first witness
   *  that PROVES a track exists, which is strictly before any duck for this call
   *  can have happened — so it can never countermand a live barge. A per-sample
   *  resume could, and is refused for that reason.
   *
   *  THIS IS A CALLER COMPENSATING FOR A MISSING ASSERTION, and it says so. The
   *  receiver asserting its own invariant is the correct home (`track.source
   *  .volume = 1.0` beside `remoteTrack = track`), approved and waiting on the
   *  next binary; a Swift change needs a Codemagic cycle and this does not. When
   *  that lands, this stays as the belt: a future path that builds a track
   *  without telling the web half is covered by the receiver, and one that tells
   *  the web half without setting the volume is covered here. */
  private unityAsserted = false;

  /** The engine's `trackMs`, last seen on `mediaStats`. -1 until `didAdd` has run
   *  — and on any binary predating that mark, which is why it is a number with a
   *  stated absent value rather than an optional that reads as "not measured yet"
   *  and "this build cannot measure it" at once. */
  private lastTrackMs = -1;

  /** The box received one. From here the peer speaks for the track. */
  notePeerOwnsPlayback(): void {
    this.peerOwnsPlayback = true;
  }

  /** May this window stop asserting? Only with BOTH: a binary that can send the
   *  verb, and a box that has confirmed one arrived. */
  private suppressWindowAssert(): boolean {
    return nativePlaybackVerb() && this.peerOwnsPlayback;
  }

  constructor(opts: MediaEngineOpts) {
    this.opts = opts;
  }

  private async wire(): Promise<void> {
    const add = async (event: string, fn: (d: any) => void) => {
      this.listeners.push(await plugin.addListener(event, fn));
    };
    // `mediaStats` arrives at 10 Hz and the channel opens once, so the mark is
    // latched here rather than re-stamped on every poll. Scoped to this wiring:
    // a superseding engine wires afresh and measures its own channel.
    let channelMarked = false;
    await add('userEndUndelivered', () => {
      // THE NATIVE END COULD NOT LEAVE (Ghost round 5, finding 2). No control
      // channel existed, so the box has NOT been told. Never silent: an
      // undelivered end that looks delivered is the fault this lane has already
      // paid for once, and the socket relay is what actually delivers here.
      this.opts.onTransport({
        state: 'end_undelivered', transport: 'webrtc_control',
        detail: 'no control channel yet — the end must go over the socket',
      });
    });
    await add('boxEndedCall', () => {
      // THE BOX ENDED IT, over the data channel, possibly while backgrounded
      // (F3's inbound verb). Surfaced as a transport fact; the session's own
      // call_state:null is still what clears `boxCall` when the socket is alive.
      this.opts.onTransport({
        state: 'box_ended', transport: 'webrtc_control', detail: 'the box ended the call',
      });
    });
    await add('mediaState', (d) => {
      const state = String(d?.state ?? '');
      const transport = String(d?.transport ?? 'webrtc');
      // ── ONLY THE PEER TRANSPORT MAY MOVE PEER LIVENESS (2026-08-10) ──────
      //
      // This was `this.connected = transport === 'webrtc' && state === 'connected'`
      // — unconditional, so EVERY mediaState event rewrote the flag, including
      // events about a different transport. The control channel opens with
      // `{state: 'connected', transport: 'webrtc_control'}`, which made the
      // expression false and turned peer liveness OFF a few hundred
      // milliseconds into every call, for the rest of its life. Channel-open
      // always follows peer-connected, so `connected` was false on every
      // healthy call.
      //
      // WHAT IT COST: `hasLivePeer()` reads this, `session_start` sends it as
      // `has_live_peer`, and the box decides `keep_media` from it. FORTY-THREE
      // adopts in the entire audit, every one `keep_media=False`. Every app
      // switch, every return, every reconnect tore down a working peer and
      // renegotiated — which is why the break came the instant the owner tapped
      // back in. It only became visible once backgrounding worked: before that
      // the peer really had died, so renegotiating was correct and invisible.
      //
      // THE RULE, SPECIFIED NOT INFERRED: update `connected` ONLY when the event
      // is about the peer transport; forward EVERY event unchanged regardless.
      //
      // EVERY SOURCE IS ENUMERATED AND EACH IS DELIBERATE — do NOT collapse
      // these into one condition, because they are what bound the stale-true
      // risk this fix turns on:
      //   awaiting_activation  no transport -> defaults 'webrtc' -> clears false
      //   start_cancelled      no transport -> defaults 'webrtc' -> clears false
      //   closed               no transport -> defaults 'webrtc' -> clears false
      //   new/connecting/connected/disconnected/failed/closed  'webrtc'
      //                                     -> true ONLY for 'connected'
      //   connected            'webrtc_control' -> MUST NOT TOUCH peer liveness
      //
      // The `?? 'webrtc'` default is load-bearing: it is what keeps the three
      // no-transport states clearing the flag. Removing it would leave a dead
      // peer reported live.
      if (transport === 'webrtc') {
        this.connected = state === 'connected';
      }
      // UNCONDITIONAL, AND OUTSIDE THE GUARD. The transport stream is unchanged
      // in behaviour: every event still reaches the surface exactly as before,
      // including the control-channel one. Only the liveness flag stopped
      // listening to events that were never about it.
      this.opts.onTransport({ state, transport, detail: d?.reason ? String(d.reason) : undefined });
      if ((state === 'failed' || state === 'disconnected') && !this.closing) {
        this.opts.onResumeNeeded(`native_${state}`);
      }
    });
    await add('playbackState', (d) => {
      // ── WHO SPEAKS FOR THE TRACK (Fix 2, owner-ruled 2026-08-10) ────────
      //
      // On a binary that carries the `playing` verb the assertion has ALREADY
      // reached the box, over the peer's own control channel, from the layer
      // that knows `audioActive && peer connected`. Putting it on the socket
      // as well would be two writers agreeing by ordering — the exact property
      // the build-19 mute round was built to remove — and the socket copy is
      // the weaker one: it dies with the WebView, which is the whole reason
      // this moved. So on that binary this event is a READOUT, not a claim.
      //
      // On every older binary nothing has reached the box, so the socket
      // assertion stays exactly as it is today. The flag is what keeps a
      // reload — which arrives without a rebuild — from silencing the
      // transcript on a binary that cannot speak for itself.
      const rendering = !!d?.rendering;
      this.lastRendering = rendering;
      // ON THE READOUT EITHER WAY (2026-08-10). Suppressing the socket write is
      // not a reason to suppress the FACT: the readout must be able to say who
      // is speaking for the track and what they said, or an absent assertion and
      // an unmeasured one look identical — which is exactly how a call in which
      // nothing ever asserted read as green.
      probeNotePlaybackAssert(rendering, this.suppressWindowAssert() ? 'peer' : 'window');
      if (this.suppressWindowAssert()) return;
      this.opts.onPlaybackAssert({
        blocked: !rendering,
        detail: String(d?.detail ?? ''),
      });
    });
    await add('playingUndelivered', (d) => {
      // ── THE WITNESS (owner ruling 2026-08-10) ─────────────────────────────
      //
      // Every crossing of the control channel reports its own failure. `end` and
      // `mute` have had this since build 19; the assertion shipped without it and
      // the cost was a build and a call nobody could read. It goes THREE places
      // because each answers a different question: the readout (what is wrong
      // right now), the transport stream (visible, never an error — an
      // undelivered assertion must not invite a retry tap), and the box's audit
      // (what happened, after the fact, from the box's own record).
      const detail = `playing=${!!d?.playing} audioActive=${!!d?.audioActive}`
        + ` peer=${!!d?.peerConnected} chan=${!!d?.hasChannel}`
        + ` open=${!!d?.channelOpen} state=${String(d?.readyState ?? '?')}`
        + ` sendAttempted=${!!d?.sendAttempted}`;
      probeNotePlaybackUndelivered(detail);
      this.opts.onTransport({
        state: 'playback_assert_undelivered', transport: 'webrtc_control', detail,
      });
      this.opts.onPlaybackUndelivered?.(detail);
    });
    await add('firstRtp', (d) => {
      this.opts.onTransport({
        state: 'first_rtp', transport: 'webrtc_media', detail: String(d?.ms ?? ''),
      });
      // AND INTO THE READOUT. The Swift has always known this — it fires only on
      // outbound-rtp.packetsSent > 0 (RexyMediaEngine.swift:390) — but it went
      // only to a transport event, which the MEDIA line does not read. So the
      // line rendered `RTP —` and then `RTP NONE` through an 80-second call the
      // box received RTP from at +2.6s and forwarded to the daemon 2ms later.
      // The measurement existed on the device the whole time; nothing carried it.
      const ms = Number(d?.ms ?? -1);
      if (Number.isFinite(ms) && ms >= 0) probeNoteFirstRtp(ms);
    });
    await add('level', (d) => {
      const level = Number(d?.level ?? 0);
      if (Number.isFinite(level)) {
        this.opts.onLevel?.(level);
        // Same field the browser path reads off media-source.audioLevel, arriving
        // by a different road. PEAK is what answers "did the microphone ever
        // produce anything", and it read `—` on native only because this value
        // stopped at the meter instead of also reaching the probe.
        probeNoteLevel(level);
      }
    });
    await add('playoutWitness', (d) => {
      // THE DRAIN WITNESS (Q3). The structural argument says NetEq is pulled
      // every 10ms regardless of gain, so a duck cannot stall playout. If that
      // were ever wrong, `delta` would read 0 across the duck and jump after —
      // which is the one shape that would disprove it, on real hardware, on
      // every call rather than in one captured run.
      // A COUNT IS NOT SECONDS. This rendered `rx 448320.00s` on 2026-08-07 —
      // totalSamplesReceived is a SAMPLE COUNT and the `s` was decoration, which
      // is why the number read as impossible. It was in fact exactly right:
      // 448320 samples is 9.34s at 48kHz, and the Δ4800 per 100ms poll is
      // real-time to the sample. The unit is dropped rather than converted,
      // because the sample rate is not among the fields the engine emits and
      // this line will not invent one to make itself readable.
      // THE GAIN RIDES WITH IT (build 20). Δ4800 proves the playout CLOCK runs; it
      // says nothing about audibility, because the gain is applied per frame after
      // the NetEq pull. Build 20 came back real-time and silent, and no field on
      // this line could separate a stuck duck from a wrong route — so the number
      // that decides it prints beside the number that raised the question.
      // A DASH IS "NO REMOTE TRACK", NOT "ZERO GAIN": the engine sends -1 when
      // there is no track to read, and the two are different findings.
      const gain = Number(d?.remoteGain ?? -1);
      probeNoteNative(`rx ${Number(d?.totalSamplesReceived ?? 0).toFixed(0)} samples`
        + ` Δ${Number(d?.delta ?? 0).toFixed(0)}`
        + ` gain ${Number.isFinite(gain) && gain >= 0 ? gain.toFixed(2) : '—'}`);
      // ── AND ON TO THE BOX (build 31) ──────────────────────────────────────
      //
      // The line above is where this number has stopped since build 20: the
      // readout, and nowhere else. The probe ENDS at 25s — it is a negotiation
      // instrument — so on 2026-08-10, when she went silent minutes into a call,
      // the one field that could have named the cause had already stopped being
      // written. The box was never told at all.
      //
      // The auditor decides which samples are worth a row (transitions, and a
      // decimated tick while the gain is held below unity) and returns null for
      // the rest, so this stays a 10Hz read and never a 10Hz emit.
      const auditRow = gainAuditSample(gain, Number(d?.totalSamplesReceived ?? -1));
      // `trackMs` is attached HERE rather than in the auditor: gainAudit.ts reaches
      // no engine and must be handed everything it reports, and this is the only
      // site that holds both the row and the mark.
      if (auditRow) this.opts.onGainAudit?.({ ...auditRow, trackMs: this.lastTrackMs });
      // ── ASSERT UNITY THE FIRST TIME A TRACK IS PROVEN TO EXIST ────────────
      //
      // `gain >= 0` is the proof: the engine sends -1 when there is no track to
      // read, so a non-negative reading is the track itself, not an inference.
      // AFTER the sample above, deliberately — the honest zero is recorded first
      // and the ramp that lifts it is the next row, so the trail shows the fault
      // and its correction rather than hiding the fault behind the fix.
      // See `unityAsserted` for why this is once-per-peer and cannot fight a barge.
      if (!this.unityAsserted && gain >= 0) {
        this.unityAsserted = true;
        this.resume();
      }
    });
    await add('mediaStats', (d) => {
      // ── THE PAYLOAD NOBODY WAS LISTENING TO (build 20) ────────────────────
      //
      // RexyMediaEngine has emitted `mediaStats` at 10 Hz since 3b, carrying the
      // channel-open moment with a comment explaining that it travels here rather
      // than separately "because a field the readout has to fetch from a second
      // place is a field that can disagree with itself". It was right about the
      // design and there was no subscriber, so `channelMs` was measured on the
      // device, put on the wire ten times a second, and dropped — and the readout
      // rendered `dc —`, which means NOBODY LOOKED. It read as though the control
      // channel might be absent, which would have made mute and end routeless
      // while backgrounded; it was only ever unmeasured.
      //
      // Same fault as `firstRtp` above and as the 3c regex: the sender was built,
      // the receiver never was, and the blank field was read as a finding.
      //
      // MARKED ON THE JS CLOCK, DELIBERATELY. The engine's own `channelMs` counts
      // from its `t0`, while `mic`/`offer`/`ans` on this line are all stamped from
      // `probeBegin`. Injecting the native figure would put one field on a
      // different origin from its neighbours, which is worse than the ≤100ms of
      // poll granularity this costs. It answers open-vs-absent either way.
      const ms = Number(d?.channelMs ?? -1);
      if (Number.isFinite(ms) && ms >= 0 && !channelMarked) {
        channelMarked = true;
        probeMark('channelMs');
      }
      // ── THE OTHER THREE FIELDS (build 21, Ghost finding 4) ────────────────
      // Build 20 wired `channelMs` out of this payload and claimed `samples` and
      // the pair counts with it. They were still dropped — the same
      // sender-built-receiver-never-was fault, one field deeper, inside the very
      // commit that named the pattern. Nothing on this path polls getStats
      // (`probeAttach` needs a JS peer and the peer lives in Swift), so these are
      // the only road those fields have.
      probeNoteNativeStats({
        totalSamples: Number(d?.samplesDuration ?? -1),
        pairsTotal: Number(d?.pairsTotal ?? NaN),
        pairsSucceeded: Number(d?.pairsSucceeded ?? NaN),
      });
      // ── AND THE ONE FIELD THAT IS NOT FOR THE READOUT ─────────────────────
      // `trackMs` travels on this payload beside `channelMs`, but its consumer is
      // the GAIN ROW, not the probe: the probe ends at 25s and this question is
      // asked of a whole call. Held here and stamped onto the rows that report a
      // gain, so `_r8` can compare "when the track was taken" against "the first
      // sample anyone could read" without a human lining up two clocks.
      const tms = Number(d?.trackMs ?? -1);
      if (Number.isFinite(tms)) this.lastTrackMs = tms;
    });
    await add('playbackDuckRefused', () => {
      // ── THE SIXTH INSTANCE, CLOSED (Ghost, build 22) ─────────────────────
      // Swift emitted this from the moment Part B existed and nothing
      // subscribed — the same native-emits/web-never-subscribes fault as the
      // route event, the data channel, firstRtp and mediaStats. It matters more
      // than most: a refused duck is the evidence that turn scoping is working,
      // and silent it is indistinguishable from a duck that never fired.
      probeNoteDuckRefused();
    });
    await add('userMuteUndelivered', () => {
      // Emitted since build 19, never subscribed. The FUNCTIONAL contract is
      // sendMute's Bool return, which the native caller still honours — this is
      // the diagnostic half, and it was going nowhere.
      probeNoteMuteUndelivered();
    });
    await add('mediaError', (d) => {
      this.opts.onTransport({
        state: 'failed', transport: 'webrtc',
        detail: String(d?.reason ?? 'native media error'),
      });
    });
  }

  async open(): Promise<{ type: string; sdp: string }> {
    await this.close('renegotiate');
    this.opts.assertLive('renegotiate');
    this.closing = false;
    probeBegin('native');
    // A NEW PEER INHERITS NO CAUSE. The gain, the turn and the duck all die with
    // the peer in Swift (`duckedTurn = nil`); the audit's memory of what put the
    // gain where it is has to die on the same boundary, or the first silence of
    // this call would be attributed to the last call's barge.
    gainAuditReset();
    // Same boundary, same reason: unity is asserted per PEER, and the new peer's
    // track has not been seen yet. Reset beside the audit reset so the two facts
    // about "this call has not started measuring" cannot drift apart.
    this.unityAsserted = false;
    // Same boundary again: a mark belonging to the previous peer must not ride a
    // new peer's rows. It returns to the stated absent value until the engine
    // reports one for THIS peer.
    this.lastTrackMs = -1;
    await this.wire();
    this.opts.assertLive('wire');
    // ── THE ORDERING, JOINED STRUCTURALLY (Ghost round 5) ─────────────────
    // CallKit is reported HERE, in the negotiation sequence, immediately before
    // media — NOT from a React effect keyed off `boxCall`.
    //
    // It was the effect, and the effect could not win: startSession() is
    // followed straight by negotiateWebRtc() -> engine.open() -> startMedia(),
    // while `boxCall` is only set after connectVoiceDaemon() RESOLVES. So
    // pendingActivationEpoch was always nil at startMedia time, the engine never
    // deferred, and capture and the peer opened before CallKit had handed the
    // session over — the exact fault this stage exists to prevent. The
    // awaitingEpoch mechanism was right; nothing armed it in time.
    //
    // THE RAIL STILL HOLDS, and structurally: open() is only reached from
    // runNegotiation(), which runs only after startSession() succeeded — so the
    // BOX HAS ALREADY CONFIRMED THE CALL before CallKit is told. Both paths that
    // negotiate (the start path and resumeTransport) share that property, so
    // there is no route to this line without the box's confirmation.
    //
    // ── THE ONLY REPORTER (build 19, Ghost round 4) ───────────────────────
    // PhoneHost used to report too, on the claim — written here — that "the
    // controller refuses a second CallKit call, so PhoneHost's effect reporting
    // afterwards costs nothing". IT DID NOT COST NOTHING. The controller's
    // idempotence began at `callId`, which is assigned only in the transaction
    // completion, so two callers before that point each bumped the epoch, and the
    // first one's completion then went stale while the second had not yet armed.
    // Media opened with no activation pending and none coming.
    //
    // The controller now claims the start before it mutates anything, so a second
    // caller attaches instead of issuing — but the second caller is gone anyway.
    // THIS one survives because it is the one that can be AWAITED before
    // startMedia; PhoneHost's effect cannot fire until `boxCall` resolves, which
    // is after this sequence has already begun. That is why the report moved here
    // in the first place, and it is why it stays here alone.
    //
    // ── THE ARM IS GONE (build 19) ─────────────────────────────────────────
    // `callKitArm()` read localStorage BEFORE the constant, so a persisted
    // `suppressed` outranked the flip and would have shipped a complete build that
    // reported nothing. The module is deleted rather than flipped: a value can be
    // cleared and re-set, a reader that does not exist cannot be.
    publishStartResult({ state: 'requested' });
    try {
      // Awaited: the arming happens in startCall's completion, so returning
      // before it resolves would leave the activation unarmed at startMedia and
      // reproduce the very race this replaces.
      const started = await startNativeCall('Rexy');
      publishStartResult({ state: 'ok', epoch: started ? started.epoch : 0 });
    } catch (e) {
      publishStartResult({ state: 'rejected', error: String((e as Error)?.message ?? e) });
      // iOS refused. The call is untouched and still works; only the system-call
      // presentation failed. NOTHING WAS TAKEN: the audio contract is established
      // inside the transaction's success block, so a refused start leaves WebRTC
      // owning activation and the call audible. (Before build 19 this comment
      // claimed the same thing and it was false — the contract ran first and was
      // never given back, which is what made a refused start a SILENT call.)
      // PhoneHost surfaces the rejection; this must not take the call down.
    }
    this.opts.assertLive('callkit');
    // ── THE ROUTE'S INITIAL VALUE, FROM JS (owner ruling 2026-08-07 (b)) ────
    // `.defaultToSpeaker` unconditional: "I want her loud when I tap, not loud
    // after I find a button." It lives here, as a named JS constant, because the
    // owner said "if it turns out wrong on the device it is one constant and I
    // will tell you" — and in JS that is an edit and a reload, while in Swift it
    // is a Codemagic build. This arc has already paid a full build cycle once for
    // a fix that sat where nothing served it.
    //
    // Applied through the controller, which owns `categoryOptions` outright, so
    // the route has ONE writer under one lock rather than two agreeing by ordering.
    try {
      await setNativeAudioRoute(DEFAULT_SPEAKER_ROUTE);
    } catch (e) {
      // A route that would not move must not take the call down — but it must not
      // vanish either (build 21, Ghost finding 3). This `catch` was empty on the
      // reasoning that "the native side reports it", and the native side reported
      // it into an event union that did not list it and a listener that did not
      // exist. Two silent paths agreeing to be silent is how a refused route
      // became invisible in the build that went quiet.
      probeNoteRouteFailed(String((e as Error)?.message ?? e).slice(0, 80));
    }
    let reply: { sdp: string };
    try {
      reply = await plugin.startMedia({ documentId: DOCUMENT_ID });
    } catch (e) {
      // A denied or missing microphone is a typed, visible failure — never
      // silent. The native side surfaces the real NSError in the message.
      this.opts.onMicUnavailable(String((e as Error)?.message ?? e));
      throw e;
    }
    this.opts.assertLive('start_media');
    // The device said yes and the offer is fully gathered — libwebrtc gathers to
    // completion before `startMedia` resolves, the native twin of
    // waitIceGatheringComplete. Both marks land here because the native side
    // does not surface an intermediate moment, and inventing one would be a
    // number this instrument did not measure.
    probeMark('micMs', { trackState: 'native' });
    probeMark('offerMs');
    if (!reply?.sdp) throw new Error('webrtc_offer_missing');
    return { type: 'offer', sdp: reply.sdp };
  }

  async accept(answerSdp: string): Promise<void> {
    await plugin.acceptAnswer({ sdp: answerSdp });
    this.opts.assertLive('remote_description');
    const cands = countCandidates(answerSdp);
    probeMark('answerMs', {
      remoteCandidates: cands?.unique,
      remoteCandidateLines: cands?.lines,
    });
    // The control channel opens on the native side; `channelMs` is marked from
    // its own event rather than guessed at here.
    this.opts.assertLive('control_channel');
  }

  isConnected(): boolean {
    return this.connected;
  }

  /** ALWAYS NULL, AND THAT IS THE POINT. libwebrtc owns the microphone; there is
   *  no MediaStream to hand over, and manufacturing one would mean opening a
   *  second capture. The hook's existing `if (micStream)` branch takes the
   *  `else` and reads the level from `onLevel` instead. */
  micStream(): MediaStream | null {
    return null;
  }

  /** ── THE GAIN COMMANDS REPORT (Ghost, build 21) ─────────────────────────
   *
   *  Both of these ended `.catch(() => {})`. A resume that failed said nothing,
   *  so the call went silent while every field on the readout stayed green —
   *  the identical shape to the swallowed route failure and the unwired dc
   *  field. A failed DUCK is the milder direction (she talks over the owner);
   *  a failed RESUME is how a call dies quietly, so it is named separately. */
  duck(turnKey?: string): void {
    const issued = plugin.duckPlayback({ ducked: true, turnKey });
    void issued.catch((e) => probeNoteGainFailed(`duck: ${String((e as Error)?.message ?? e)}`));
    this.auditGainCommand('duck', issued, turnKey);
  }

  resume(turnKey?: string): void {
    const issued = plugin.duckPlayback({ ducked: false, turnKey });
    void issued.catch((e) => probeNoteGainFailed(`resume: ${String((e as Error)?.message ?? e)}`));
    this.auditGainCommand('resume', issued, turnKey);
  }

  /** ── THE COMMAND, AND WHETHER IT CAME BACK (build 31) ───────────────────
   *
   *  THE CALL IS ISSUED BEFORE THIS RUNS, and that ordering is the rule, not a
   *  preference: nothing may sit between a barge and the drop it commands. The
   *  witness observes a command already on its way; it never gates one.
   *
   *  TWO ROWS PER COMMAND, and the second is the point. The first says a duck
   *  was issued and names the turn. The second says the bridge came back, and
   *  whether it came back an error. A `command` row with no `settled` twin is a
   *  command that never returned at all — the third possibility, alongside a
   *  stuck duck and a stuck mute, that nothing in this lane could previously see.
   *
   *  `probeNoteGainFailed` above is untouched and still holds the first `catch`.
   *  This attaches its own handlers to the same promise rather than replacing
   *  them, so the readout behaves exactly as it did. */
  private auditGainCommand(cause: 'duck' | 'resume', issued: Promise<void>, turnKey?: string): void {
    const emit = this.opts.onGainAudit;
    if (!emit) return;
    emit(gainAuditCommand(cause, { turnKey }));
    void issued.then(
      () => emit(gainAuditCommand(cause, { kind: 'settled', delivered: 'yes', turnKey })),
      (e) => emit(gainAuditCommand(cause, {
        kind: 'settled', delivered: 'no', turnKey,
        detail: String((e as Error)?.message ?? e).slice(0, 120),
      })),
    );
  }

  /** Gate the level poll. The surface discards the value outside
   *  `listening`/`user_speaking`, so polling there is battery spent on a number
   *  nobody reads. */
  setLevelReporting(enabled: boolean): void {
    void plugin.setLevelReporting({ enabled }).catch(() => {});
  }

  reassertPlayback(): void {
    // ── THE DEFECT THIS USED TO BE (found in review, 2026-08-10) ──────────
    //
    // This method asserted NOTHING. It called setLevelReporting and its comment
    // said "the native side answers from the track it actually holds" — but the
    // native side's only playback emission fired on `didAdd rtpReceiver`, which
    // is precisely what does NOT fire on an adopt. So on the native path an
    // adopt-with-keep-media left the box at not-yet-asserted and every mark went
    // un-acked: she spoke, the phone played it, and none of it was recorded as
    // heard. Third instance in this file of a sender built with no receiver.
    //
    // On a `playing`-verb binary the question no longer arises: the peer OWNS
    // the assertion at the box, an adopt does not disturb it, and the box's
    // unassert is a no-op while that ownership stands. Nothing needs restating
    // because nothing was dropped.
    //
    // On an older binary this restates the last fact the native side stated —
    // the same thing the browser engine does with its own graph. A binary that
    // has said nothing yet reports BLOCKED, which is the safe direction and the
    // same answer the browser gives for a window with no audible graph.
    void plugin.setLevelReporting({ enabled: true }).catch(() => {});
    if (this.suppressWindowAssert()) return;
    if (this.lastRendering === true) {
      this.opts.onPlaybackAssert({ blocked: false, detail: '' });
    } else {
      this.opts.onPlaybackAssert({
        blocked: true,
        detail: this.lastRendering === false
          ? 'native side reported not rendering'
          : 'native side has not reported playback on this peer yet',
      });
    }
  }

  async close(reason: string): Promise<void> {
    this.closing = true;
    this.connected = false;
    // The stated fact dies with the peer it described. A new peer that has said
    // nothing must not inherit the last one's answer — the same rule the box
    // applies by building a fresh track per offer.
    this.lastRendering = null;
    // AND SO DOES THE OWNERSHIP. The box builds a fresh track per offer, owned
    // by the window and not-yet-asserted, so the right to stop asserting has to
    // be earned again on the new peer rather than inherited from the old one.
    this.peerOwnsPlayback = false;
    probeEnd(reason);
    for (const l of this.listeners) { try { l.remove(); } catch {} }
    this.listeners = [];
    try { await plugin.stopMedia({ reason }); } catch { /* already gone */ }
  }
}
